<template>
  <BittsModal
    class="c-invite-team-member"
    :title="`Invite teammate${approvingSingleInviteRequest ? '' : 's'} to Crossbeam`"
    :primary-button-text="`Send invite${approvingSingleInviteRequest ? '' : 's'}`"
    :loading="loading || !ready"
    :hide-footer="loading || !ready"
    :action-loading="isSendingInvites"
    :width="600"
    :disabled="sendInvitesDisabled"
    :use-mask-to-close="true"
    @closed="handleModalClosed"
    @action="handleInviteSubmitted"
  >
    <!-- User management disabled banner -->
    <UserManagementDisabledBanner v-if="!userManagementEnabledForOrg" />
    <div v-if="isAdminPanelUser && userManagementEnabledForOrg">
      <BittsSelect
        v-model="selectedUserOnBehalfOf"
        :options="userWithManageMembersPermissionList"
        :allow-clear="true"
        data-testid="send-invite-on-behalf-to-user-selector"
        placeholder="Select user"
        :form-label="{
          title: 'Send invite on behalf of',
        }"
        option-type="entity"
        class="mb-16"
      />
      <BittsAlert
        v-if="!!selectedUserOnBehalfOf"
        color="warning"
        :message="alertMessageOnBehalf"
      ></BittsAlert>
    </div>
    <div v-if="userManagementEnabledForOrg">
      <SSOToggle v-model="isSSO" />
      <UserRequestCard v-if="approvingSingleInviteRequest" />
      <SelectSeatType
        :auths="emails"
        :picked-seat="pickedSeat"
        :disable-seat-checks="overSeatLimit && pickedSeat.value !== INTEGRATION"
        @open-add-seats-modal="onAddSeats"
        @seat-selected="handleChangeSeat"
        :class="{
          'mt-16': approvingSingleInviteRequest,
        }"
      />
      <AddEmails
        v-if="!approvingSingleInviteRequest"
        data-testid="add-emails"
        class="add-emails"
        :disabled="approvingSeatRequest"
        :invite-request-id="inviteRequestId"
        :note="addEmailsNote"
        @emails="handleAddEmail"
      />
      <div v-if="!isIntegrationSeat" class="flex flex-col gap-16 w-full mt-24">
        <OutOfSeatsCTA
          :seat-type="pickedSeat.value"
          :user-count="outOfSeatCount"
        />
        <SelectCoreRole
          v-if="pickedSeat.value === CORE"
          v-model="pickedCoreRole"
          :picked-seat="pickedSeat"
        />
        <SelectSalesRole
          v-if="!isFreeTier"
          v-model="pickedSalesRole"
          :picked-seat="pickedSeat"
        />
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsAlert, BittsModal, BittsSelect } from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';
import { useScreenSize } from '@crossbeam/pointbreak';
import { AddSelectProperties } from '@crossbeam/types';

import { useHead } from '@unhead/vue';
import { AxiosError } from 'axios';
import { pluralize } from 'humanize-plus';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import AddEmails from '@/components/team/AddEmails.vue';
import OutOfSeatsCTA from '@/components/team/OutOfSeatsCTA.vue';
import UserRequestCard from '@/components/team/seat-requests/UserRequestCard.vue';
import SelectCoreRole from '@/components/team/SelectCoreRole.vue';
import SelectSalesRole from '@/components/team/SelectSalesRole.vue';
import SelectSeatType from '@/components/team/SelectSeatType.vue';
import SSOToggle from '@/components/team/SSOToggle.vue';
import UserManagementDisabledBanner from '@/components/team/UserManagementDisabledBanner.vue';

import useAuth from '@/composables/useAuth';
import useHasFeature from '@/composables/useHasFeature';
import useIteratively from '@/composables/useIteratively';
import useSeats from '@/composables/useSeats';
import { SSO_ONLY } from '@/constants/me';
import { CORE, INTEGRATION, SALES, V4_SEAT_OPTIONS } from '@/constants/team_v4';
import { captureException } from '@/errors';
import {
  allReady,
  useBillingStore,
  useFlashesStore,
  useRolesStore,
  useSeatRequestsStore,
  useTeamStore,
} from '@/stores';
import { TeamSeatType } from '@/types/team';

const { inviteRequestId = null } = defineProps<{
  inviteRequestId?: string | null;
}>();

useHead({ title: 'Invite - Team - Crossbeam' });

const loading = ref(false);
const router = useRouter();

const seatRequestsStore = useSeatRequestsStore();
const flashesStore = useFlashesStore();
const rolesStore = useRolesStore();
const teamStore = useTeamStore();
const billingStore = useBillingStore();

const { salesSeatRole, integrationSeatRole } = storeToRefs(rolesStore);

async function onAddSeats() {
  await router.push({
    name: 'team',
    query: {
      open_add_seats: 'true',
    },
  });
}

const { isFreeTier } = storeToRefs(billingStore);

const { iteratively } = useIteratively();
const {
  currentOrg,
  currentUser,
  isAdminPanelUser,
  userManagementEnabledForOrg,
} = useAuth();
const {
  seatsRemainingMap,
  coreSeatCount,
  salesSeatCount,
  atCoreSeatLimit,
  atSalesSeatLimit,
  atIntegrationSeatLimit,
  canUseIntegrationSeats,
} = useSeats();
const { canGoOverSeatQuota } = useHasFeature();
const { isMobile } = useScreenSize();

const route = useRoute();

const ready = allReady(teamStore, rolesStore, seatRequestsStore);

const selectedUserOnBehalfOf = ref(null);
const userWithManageMembersPermissionList = computed(() =>
  teamStore.authorizations
    .filter(({ role_id, user }) => {
      const role = rolesStore.allRolesWithIsCustom.find(
        ({ id }) => id === role_id,
      );
      return (
        role?.permissions.some(
          (permission) => permission.id === 'manage:members',
        ) && user.email !== currentUser.value.email
      );
    })
    .map(({ user }) => ({
      ...user,
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    })),
);
const representedUser = computed(() =>
  userWithManageMembersPermissionList.value.find(
    (user) => user.id === selectedUserOnBehalfOf.value,
  ),
);
const alertMessageOnBehalf = computed(() => {
  return `Make sure you have the authorization of ${representedUser.value?.label} to send this invite`;
});

/* Picking a seat type (core/sales) */
const options = JSON.parse(JSON.stringify(V4_SEAT_OPTIONS));
const querySeatType = route.query.seat_type;
const initialSeatType = options.find(({ value }: { value: TeamSeatType }) => {
  if (!value) return false;
  if (value === INTEGRATION && !canUseIntegrationSeats.value) return false;
  if (querySeatType) return value === querySeatType;
  return seatsRemainingMap.value[value] > 0;
});
const pickedSeat = ref(initialSeatType || {});
/* Updates the picked seat, clears role selections incompatible with the new seat
   type, and syncs the choice to the URL query so the state survives a refresh */
function handleChangeSeat(newSeat: AddSelectProperties<{ u: TeamSeatType }>) {
  pickedSeat.value = newSeat;
  if (newSeat.value === SALES || newSeat.value === INTEGRATION)
    pickedCoreRole.value = null;
  if (newSeat.value === INTEGRATION) pickedSalesRole.value = null;
  router.replace({
    name: route.name,
    query: { ...route.query, seat_type: pickedSeat.value.value },
  });
}

/* Emails */
const emails = ref<string[]>(
  typeof route.query.emails === 'string' ? route.query.emails.split(',') : [],
);
const seatRequest = computed(() => {
  if (!emails.value?.length || !emails.value[0]) return null;
  return seatRequestsStore.getSeatRequestByEmail(emails.value[0]);
});
async function handleAddEmail(newEmails: string[]) {
  emails.value = newEmails;
  if (approvingSeatRequest.value && !seatRequest.value) {
    // If we cannot find the seat request, then close the modal
    flashesStore.addSuccessFlash({
      message: 'Request no longer exists',
      description: 'Another admin responded to this request already',
    });
    await handleModalClosed();
    return;
  }
  router.replace({
    name: route.name,
    query: { ...route.query, emails: newEmails.join(',') },
  });
}

/* Roles */
const pickedCoreRole = ref(null);
const pickedSalesRole = ref(null);

/* SSO */
const ssoRequired = computed(() => currentOrg.value.login_method === SSO_ONLY);
const isSSO = ref(ssoRequired.value);

/* Submitting invites */
const isSendingInvites = ref(false);

const isIntegrationSeat = computed(
  () => pickedSeat.value.value === INTEGRATION,
);
const addEmailsNote = computed(() =>
  isIntegrationSeat.value
    ? 'Use a shared service account (e.g. integrations@yourcompany.com) rather than a personal email. If that person leaves or changes roles, your CRM connection will break.'
    : undefined,
);

const overSeatLimit = computed(() => {
  if (canGoOverSeatQuota.value) return false;
  if (pickedSeat.value.value === CORE) return atCoreSeatLimit.value;
  if (pickedSeat.value.value === SALES) return atSalesSeatLimit.value;
  if (pickedSeat.value.value === INTEGRATION)
    return atIntegrationSeatLimit.value;
  return false;
});

const sendInvitesDisabled = computed(() => {
  if (!userManagementEnabledForOrg.value) return true;
  if (emails.value.length === 0) return true;
  if (overSeatLimit.value) return true;
  if (isIntegrationSeat.value && !integrationSeatRole.value) return true;
  if (isIntegrationSeat.value) return false;
  if (!isFreeTier.value && pickedSalesRole.value === null) return true;
  if (!pickedSalesRole.value && !pickedCoreRole.value) return true;
  return false;
});

const handleInviteSubmitted = async () => {
  isSendingInvites.value = true;
  let coreRole;
  if (isIntegrationSeat.value) {
    coreRole = integrationSeatRole.value?.id || null;
  } else if (pickedCoreRole.value) {
    coreRole = pickedCoreRole.value;
  } else {
    coreRole = salesSeatRole.value?.id || null;
  }
  try {
    const invites = await teamStore.sendInvites({
      emails: emails.value,
      // @ts-expect-error: this needs to be fixed in the types
      crossbeamRoleId: coreRole,
      salesEdgeRoleId: pickedSalesRole.value,
      isSSO: isSSO.value,
      onBehalfOf: selectedUserOnBehalfOf.value
        ? representedUser.value?.id
        : null,
    });
    const seatLabel = isIntegrationSeat.value
      ? 'Integration'
      : pickedCoreRole.value && pickedCoreRole.value !== salesSeatRole.value?.id
        ? 'Full Access'
        : 'Sales';
    flashesStore.addSuccessFlash({
      message: `${seatLabel} ${pluralize(emails.value.length, 'invitation')} sent`,
    });

    const promises = [teamStore.refreshTeamStore()];
    if (approvingSeatRequest.value)
      promises.push(seatRequestsStore.refreshSeatRequestsStore());
    await Promise.all(promises);

    teamStore.refreshTeamStore();
    invites.forEach(({ email, role_id: roleId }) => {
      const iterativelyPayload = {
        teammate_email: email,
        seat_type: pickedSeat.value.value,
        invite_location: route.name as string,
        event_site: EVENT_SITES.TEAM_INVITE_MODAL,
      };
      if (roleId)
        // @ts-expect-error: iterativelyPayload.role is not defined in the type
        iterativelyPayload.role = rolesStore.getRoleById(roleId)?.name || '';
      iteratively.userInvitesTeammate(iterativelyPayload);
    });
  } catch (err) {
    captureException(err);
    if ((err as AxiosError)?.response?.status === 402) {
      flashesStore.addErrorFlash({ message: 'Invitation exceeds user quota' });
    } else {
      flashesStore.addErrorFlash({
        message: `Something went wrong with sending the ${pluralize(emails.value.length, 'invite')}`,
      });
    }
  } finally {
    isSendingInvites.value = false;
    billingStore.refreshBillingStore();
    handleModalClosed();
  }
};

const outOfSeatCount = computed(() => {
  if (pickedSeat.value.value === CORE) return coreSeatCount.value;
  if (pickedSeat.value.value === SALES) return salesSeatCount.value;
  return 0;
});

/* Approving access request */
const approvingSeatRequest = computed(
  () => route.name === 'approve_team_seat_request',
);
const approvingSingleInviteRequest = computed(
  () => approvingSeatRequest.value && emails.value?.length === 1,
);

/* Close modal */
async function handleModalClosed() {
  if (route?.query?.cancelDestination)
    await router.push({
      name: route.query.cancelDestination as string,
    });
  else await router.push({ name: isMobile.value ? 'home' : 'team' });
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-invite-team-member {
  :deep(.o-bitts-modal__footer) {
    @apply flex w-full items-center justify-between;
  }

  :deep(.add-emails .bitts-form-label__text) {
    @apply opacity-100!;
  }
}
</style>
